import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import {
  Search, CreditCard, Calendar, User,
  X, RefreshCw,
} from "lucide-react";
import moment from "moment-jalaali";
import useAxiosAdmin, { API_BASE } from "../lib/axiosAdmin";
import { SET_ACCESS_TOKEN } from "../../../redux/action/auth/type";

const jalaliToGregorian = (jY, jM, jD) => {
  const jYn = parseInt(jY), jMn = parseInt(jM), jDn = parseInt(jD);
  if (!jYn || !jMn || !jDn) return "";
  const m = moment(`${jYn}/${String(jMn).padStart(2, "0")}/${String(jDn).padStart(2, "0")}`, "jYYYY/jMM/jDD");
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const toJalali = (gregorian) =>
  gregorian ? moment(gregorian, "YYYY-MM-DD").format("jYYYY/jMM/jDD") : "";

// ─── تبدیل عدد به فارسی ──────────────────────────────────────────────────────
const toPersian = (num) =>
  num == null ? "" : String(num).replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

// ─── نمایش مبلغ: اگر zarinpalAmount وجود داره از زرین‌پال نشون بده
// وگرنه مبلغ ریال ذخیره‌شده رو نشون بده
// (مقدار amount در دیتابیس = price * 10 تومان به ریال)
const displayAmount = (p) => {
  if (p.zarinpalAmount != null && p.zarinpalAmount !== undefined) return p.zarinpalAmount;
  return p.amount || 0;
};

// ─── باکس انتخاب تاریخ شمسی ──────────────────────────────────────────────────
const DateBox = ({ title, value, onChange }) => {
  const now = moment();
  const parsed = value ? moment(value, "YYYY-MM-DD") : null;
  const [jY, setJY] = useState(parsed?.jYear()  ?? now.jYear());
  const [jM, setJM] = useState(parsed ? parsed.jMonth() + 1 : now.jMonth() + 1);
  const [jD, setJD] = useState(parsed?.jDate()  ?? now.jDate());

  // وقتی value از بیرون پاک میشه، فیلدها ریست بشن
  useEffect(() => {
    if (!value) {
      setJY(now.jYear());
      setJM(now.jMonth() + 1);
      setJD(now.jDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const tryCommit = (y, m, d) => {
    const g = jalaliToGregorian(y, m, d);
    onChange(g || "");
  };

  return (
    <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-purple-700 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {title}
        </span>
        {value && (
          <button
            onClick={() => onChange("")}
            className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
          >
            <X className="w-3 h-3" /> پاک
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 justify-center">
        {/* سال */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-slate-400">سال</span>
          <input
            type="number"
            min={1390} max={1420}
            value={jY}
            onChange={(e) => { const v = parseInt(e.target.value) || now.jYear(); setJY(v); tryCommit(v, jM, jD); }}
            className="w-16 px-2 py-2 text-sm border border-gray-200 rounded-lg text-center focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
          />
        </div>
        <span className="text-gray-300 mt-5 text-lg">/</span>
        {/* ماه */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-slate-400">ماه</span>
          <input
            type="number"
            min={1} max={12}
            value={jM}
            onChange={(e) => { const v = Math.min(12, Math.max(1, parseInt(e.target.value) || 1)); setJM(v); tryCommit(jY, v, jD); }}
            className="w-12 px-2 py-2 text-sm border border-gray-200 rounded-lg text-center focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
          />
        </div>
        <span className="text-gray-300 mt-5 text-lg">/</span>
        {/* روز */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-slate-400">روز</span>
          <input
            type="number"
            min={1} max={31}
            value={jD}
            onChange={(e) => { const v = Math.min(31, Math.max(1, parseInt(e.target.value) || 1)); setJD(v); tryCommit(jY, jM, v); }}
            className="w-12 px-2 py-2 text-sm border border-gray-200 rounded-lg text-center focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
          />
        </div>
      </div>

      {/* نمایش تاریخ انتخاب‌شده */}
      <div className={`text-center text-xs rounded-lg py-1.5 ${value ? "bg-purple-50 text-purple-600 font-medium" : "bg-gray-50 text-gray-400"}`}>
        {value ? `📅 ${toJalali(value)}` : "تاریخی انتخاب نشده"}
      </div>
    </div>
  );
};

const AdminPaymentsContent = () => {
  const dispatch = useDispatch();
  const axiosAdmin = useAxiosAdmin();
  const [cookies] = useCookies();

  // ─── داده ───────────────────────────────────────────────────────────────────
  const [payments, setPayments]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [total, setTotal]           = useState(0);
  const [search, setSearch]         = useState("");
  const [registrationFilter, setRegistrationFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);

  // ─── علامت‌گذاری ─────────────────────────────────────────────────────────────
  const [markedPayments, setMarkedPayments] = useState(() => {
    try { return JSON.parse(localStorage.getItem("markedPayments") || "[]"); } catch { return []; }
  });

  // ─── fetch ───────────────────────────────────────────────────────────────────
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosAdmin.get("/api/admin/payments?limit=10000");
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      // 401 توسط interceptor هندل می‌شود (refresh + redirect)
      // اینجا فقط خطاهای غیر از 401 را نشان بده
      if (err.response?.status !== 401) {
        console.error("خطا در دریافت پرداخت‌ها:", err);
        toast.error(err.response?.data?.message || "خطا در دریافت پرداخت‌ها");
      }
    } finally {
      setLoading(false);
    }
  }, [axiosAdmin]);

  // بارگذاری اولیه: همه پرداخت‌ها
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    localStorage.setItem("markedPayments", JSON.stringify(markedPayments));
  }, [markedPayments]);

  const handleSyncZarinpal = async () => {
    setSyncing(true);
    try {
      const { data } = await axiosAdmin.post("/api/admin/payments/sync-zarinpal");
      toast.success(`${toPersian(data.synced)} پرداخت زرین‌پال ثبت شد`);
      await fetchPayments();
    } catch (err) {
      console.error("خطا در همگام‌سازی زرین‌پال:", err);
      toast.error(err.response?.data?.message || "خطا در همگام‌سازی زرین‌پال");
    } finally {
      setSyncing(false);
    }
  };

  // ─── تفکیک وضعیت ثبت + جستجو ─────────────────────────────────────────────────

  const filteredAndSorted = payments
    .filter((p) => {
      // ── حذف پرداخت‌های لغو شده ──
      if (p.status === "لغو شده") return false;

      // ── ثبت شده = تیک دستی ──
      const isRegistered = markedPayments.includes(p._id);
      if (registrationFilter === "registered" && !isRegistered) return false;
      if (registrationFilter === "unregistered" && isRegistered) return false;
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (
        (p.user?.name || "").toLowerCase().includes(s) ||
        (p.user?.phone || "").includes(search) ||
        (p.paymentType || "").toLowerCase().includes(s) ||
        (p.service?.name || "").toLowerCase().includes(s) ||
        (p.datetime?.time || "").includes(search)
      );
    })
    .sort((a, b) => {
      const dateA = a.createdAt || "";
      const dateB = b.createdAt || "";
      if (dateA !== dateB) return dateA > dateB ? -1 : 1;
      return 0;
    });

  const paymentTypeColors = {
    "زرین پال":     "bg-purple-100 text-purple-700",
    "کارت به کارت": "bg-blue-100 text-blue-700",
    "دستگاه POS":   "bg-green-100 text-green-700",
    "نامعلوم":      "bg-gray-100 text-gray-700",
  };
  const statusColors = {
    "تایید شده":    "bg-green-100 text-green-700",
    "لغو شده":      "bg-red-100 text-red-700",
    "درحال پرداخت": "bg-yellow-100 text-yellow-700",
  };

  const toggleMark = (id) =>
    setMarkedPayments((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800">پرداخت‌ها</h1>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={handleSyncZarinpal}
            disabled={syncing}
            className="px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-600 text-white shadow-md hover:bg-purple-700 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> همگام‌سازی
          </button>
          {markedPayments.length > 0 && (
            <button onClick={() => setMarkedPayments([])} className="px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-all">
              پاک علامت‌ها
            </button>
          )}
          <button
            onClick={fetchPayments}
            disabled={loading}
            className="p-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-all disabled:opacity-50"
            title="بارگذاری مجدد"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          ["all", "همه"],
          ["registered", "ثبت‌شده"],
          ["unregistered", "ثبت‌نشده"],
        ].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setRegistrationFilter(value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              registrationFilter === value
                ? "bg-purple-100 text-purple-700 ring-1 ring-purple-300"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── جستجو ── */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="جستجو: نام، شماره، نوع پرداخت..."
          className="w-full pr-10 pl-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-slate-700 placeholder:text-gray-400 focus:outline-none focus:border-purple-400 focus:bg-white transition-all"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ── جدول ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-400">در حال بارگذاری...</span>
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <CreditCard className="w-12 h-12 text-slate-300 mb-3" />
            <p>هیچ پرداختی یافت نشد</p>
          </div>
        ) : (
          <>
            {/* نسخه دسکتاپ — جدول */}
            <div className="hidden md:block overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full text-[10px] text-right whitespace-normal leading-tight">
                <thead>
                  <tr className="bg-purple-50 border-b border-purple-100">
                    <th className="px-2 py-2 w-10 sm:px-2">
                      <input
                        type="checkbox"
                        checked={filteredAndSorted.length > 0 && filteredAndSorted.every((p) => markedPayments.includes(p._id))}
                        onChange={(e) => setMarkedPayments(e.target.checked ? filteredAndSorted.map((p) => p._id) : [])}
                        className="w-4 h-4 rounded border-purple-300 text-purple-600"
                      />
                    </th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">ردیف</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">مشتری</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">خدمت</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">📅 تاریخ رزرو</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">📅 تاریخ پرداخت</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">🕐 ساعت پرداخت</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">نوع پرداخت</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">مبلغ</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">وضعیت</th>
                    <th className="px-2 py-2 font-semibold text-purple-800 sm:px-2">ثبت</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.map((p, i) => (
                    <tr key={p._id} className={`border-b border-gray-50 hover:bg-purple-50/30 transition-colors ${markedPayments.includes(p._id) ? "bg-purple-50" : ""}`}>
                      <td className="px-2 py-2 sm:px-2 sm:py-2">
                        <input type="checkbox" checked={markedPayments.includes(p._id)} onChange={() => toggleMark(p._id)} className="w-4 h-4 rounded border-purple-300 text-purple-600" />
                      </td>
                      <td className="px-2 py-2 text-slate-500 sm:px-2 sm:py-2">{toPersian(i + 1)}</td>
                      <td className="px-2 py-2 sm:px-2 sm:py-2">
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="font-medium text-slate-700">{p.user?.name || "-"}</span>
                        </div>
                        <p className="text-xs text-slate-400 dir-ltr">{p.user?.phone || ""}</p>
                      </td>
                      <td className="px-2 py-2 text-slate-600 sm:px-2 sm:py-2">
                        {p.service?.name}
                        {p.service?.method && <span className="mr-1 text-xs text-slate-400">• {p.service.method}</span>}
                      </td>
                      <td className="px-2 py-2 text-slate-600 sm:px-2 sm:py-2">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="font-medium">{toPersian(moment(p.datetime?.date, "YYYY-MM-DD").format("jYYYY/jMM/jDD"))}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-slate-600 text-xs sm:px-2 sm:py-2">
                        {p.createdAt ? toPersian(moment(p.createdAt).format("jYYYY/jMM/jDD")) : "-"}
                      </td>
                      <td className="px-2 py-2 text-slate-600 text-xs sm:px-2 sm:py-2">
                        {p.createdAt ? toPersian(moment(p.createdAt).format("HH:mm")) : "-"}
                      </td>
                      <td className="px-2 py-2 sm:px-2 sm:py-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${paymentTypeColors[p.paymentType] || "bg-gray-100 text-gray-700"}`}>
                          {p.paymentType}
                        </span>
                      </td>
                      <td className="px-2 py-2 font-bold text-slate-700 whitespace-nowrap sm:px-2 sm:py-2">
                        {toPersian(displayAmount(p).toLocaleString())} ریال
                      </td>
                      <td className="px-2 py-2 sm:px-2 sm:py-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${statusColors[p.status] || "bg-gray-100 text-gray-700"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center sm:px-2 sm:py-2">
                        <button
                          onClick={() => toggleMark(p._id)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                            markedPayments.includes(p._id)
                              ? "bg-green-600 text-white hover:bg-green-700"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {markedPayments.includes(p._id) ? "✅ ثبت" : "ثبت"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* نسخه موبایل — کارت */}
            <div className="md:hidden space-y-3 p-4">
              {filteredAndSorted.map((p, i) => (
                <div key={p._id} className={`rounded-xl border p-3 space-y-2 ${markedPayments.includes(p._id) ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-purple-700">ردیف {toPersian(i + 1)}</span>
                    <input type="checkbox" checked={markedPayments.includes(p._id)} onChange={() => toggleMark(p._id)} className="w-4 h-4 rounded border-purple-300 text-purple-600" />
                  </div>
                  <div className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    {p.user?.name || "-"}
                    <span className="text-xs text-slate-400 dir-ltr">{p.user?.phone || ""}</span>
                  </div>
                  <div className="text-xs text-slate-600">
                    {p.service?.name}{p.service?.method && <span className="mr-1 text-slate-400">• {p.service.method}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="text-slate-500">📅 {toPersian(moment(p.datetime?.date, "YYYY-MM-DD").format("jYYYY/jMM/jDD"))}</span>
                    <span className="text-slate-500">🕐 {p.createdAt ? toPersian(moment(p.createdAt).format("HH:mm")) : "-"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${paymentTypeColors[p.paymentType] || "bg-gray-100 text-gray-700"}`}>{p.paymentType}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[p.status] || "bg-gray-100 text-gray-700"}`}>{p.status}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">{toPersian(displayAmount(p).toLocaleString())} ریال</span>
                    <button
                      onClick={() => toggleMark(p._id)}
                      className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                        markedPayments.includes(p._id) ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {markedPayments.includes(p._id) ? "✅ ثبت" : "ثبت"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default AdminPaymentsContent;
